import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Mail, Lock, User, LogIn } from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';
import SignInWithAppleButton from '@/components/SignInWithAppleButton';

const FACEBOOK_APP_ID = '1491260965838600';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, loginWithGoogle, refreshUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isFacebookLoading, setIsFacebookLoading] = useState(false);
  const [fbSdkReady, setFbSdkReady] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // Handle Facebook authentication with backend
  const handleFacebookAuth = async (authResponse) => {
    setIsFacebookLoading(true);
    try {
      // Log the inbound shape so we can debug production-only failures
      // (Cloudflare-wrapped 400s, missing scopes, etc.) without needing
      // a screen recording. Visible in DevTools → Console.
      // eslint-disable-next-line no-console
      console.log('[FB] Submitting Facebook auth payload:', {
        accessTokenLen: authResponse?.accessToken?.length || 0,
        userID: authResponse?.userID,
      });

      const { data, error, status } = await apiFetch('/api/auth/facebook/token', {
        method: 'POST',
        body: {
          access_token: authResponse.accessToken,
          user_id: authResponse.userID,
        },
      });

      if (error) {
        // eslint-disable-next-line no-console
        console.error('[FB] Backend response:', { status, error });
        // Surface the actual backend reason. The backend now logs
        // detailed diagnostics in /var/log/supervisor/backend.*.log.
        let msg = error || `Facebook login failed (HTTP ${status})`;
        if (status === 0) {
          msg = 'Could not reach the server. Check your connection and try again.';
        } else if (status === 502) {
          msg = `Facebook is unreachable from our server. Please try again in a moment. (${error})`;
        }
        throw new Error(msg);
      }
      
      // Store token - this is critical for subsequent API calls
      localStorage.setItem('auth_token', data.token);
      
      // Refresh auth state from server (sets user + isAuthenticated)
      if (refreshUser) await refreshUser();

      toast.success(data.is_new_user ? 'Welcome to ClearCommunication!' : 'Welcome back!');
      navigate(data.is_new_user ? '/profile?welcome=1' : '/library');
    } catch (error) {
      console.error('Facebook auth error:', error);
      toast.error(error.message || 'Facebook login failed');
    } finally {
      setIsFacebookLoading(false);
    }
  };

  // Initialize Facebook SDK
  useEffect(() => {
    window.fbAsyncInit = function() {
      window.FB.init({
        appId: FACEBOOK_APP_ID,
        cookie: true,
        xfbml: false,
        version: 'v25.0'
      });
      
      window.FB.AppEvents.logPageView();
      setFbSdkReady(true);
    };

    // Load the SDK asynchronously
    (function(d, s, id) {
      var js, fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) { return; }
      js = d.createElement(s); js.id = id;
      js.src = "https://connect.facebook.net/en_US/sdk.js";
      fjs.parentNode.insertBefore(js, fjs);
    }(document, 'script', 'facebook-jssdk'));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const result = await login(formData.email, formData.password);
      const isNewUser = result?.is_new_user || result?.data?.is_new_user;
      toast.success(isNewUser ? 'Welcome to ClearCommunication!' : 'Welcome back!');
      // Admin-created or first-time-login users go to /profile to set up
      // their avatar / display name; returning users continue to /library.
      navigate(isNewUser ? '/profile?welcome=1' : '/library');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // Custom-OAuth-first routing lives inside loginWithGoogle (AuthContext)
    // so Login + Register + any future entry point behave identically.
    loginWithGoogle();
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="container py-12">
        <div className="max-w-md mx-auto">
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">Welcome Back</CardTitle>
              <CardDescription>
                Sign in to access all phrases and features
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-4">
              {/* Google Login - High visibility with bold text */}
              <Button 
                variant="outline" 
                className="w-full border-2 border-[#4285F4] hover:border-[#357ABD] bg-white hover:bg-blue-50 text-gray-900 font-bold shadow-md transition-all"
                onClick={handleGoogleLogin}
                data-testid="google-login-btn"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span className="text-gray-900">Continue with Google</span>
              </Button>
              
              {/* Facebook Login */}
              <Button 
                variant="outline" 
                className="w-full border-2 border-[#1877F2] hover:border-[#166FE5] bg-white hover:bg-blue-50 text-gray-900 font-bold shadow-md transition-all"
                onClick={() => {
                  if (window.FB) {
                    window.FB.login(function(response) {
                      if (response.authResponse) {
                        handleFacebookAuth(response.authResponse);
                      }
                    }, { scope: 'public_profile,email', auth_type: 'rerequest' });
                  } else {
                    toast.error('Facebook SDK not loaded. Please try again.');
                  }
                }}
                disabled={!fbSdkReady}
                data-testid="facebook-login-btn"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="#1877F2">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                <span className="text-gray-900">Continue with Facebook</span>
              </Button>

              {/* Apple Sign-In (required for Apple App Store guideline 4.8) */}
              <SignInWithAppleButton />

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    Or continue with email
                  </span>
                </div>
              </div>
              
              {/* Email/Password Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      className="pl-10"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      className="pl-10"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                    />
                  </div>
                </div>
                
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? 'Signing in...' : 'Sign In'}
                  <LogIn className="ml-2 h-4 w-4" />
                </Button>
              </form>
            </CardContent>
            
            <CardFooter className="flex flex-col space-y-2">
              <Link to="/forgot-password" className="text-sm text-muted-foreground hover:text-primary">
                Forgot your password?
              </Link>
              <p className="text-sm text-muted-foreground text-center">
                Don't have an account?{' '}
                <Link to="/register" className="text-primary hover:underline">
                  Sign up
                </Link>
              </p>
              <div className="flex gap-4 text-xs text-muted-foreground mt-2">
                <Link to="/privacy" className="hover:text-primary">Privacy Policy</Link>
                <span>|</span>
                <Link to="/terms" className="hover:text-primary">Terms of Service</Link>
              </div>
            </CardFooter>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
